import { Dispatch, useEffect, useState, useRef } from 'react';
import { CustomerGearResponse, CustomerProfile, GearFilters, QueryParams } from 'types';
import { getCustomerGearFilters, getCustomerGearList } from 'api';
import { formatNumberToLetteredValue } from 'utils/formatters';
import { GearCard } from 'components/GearCard';
import { LazyLoader } from 'components/LazyLoader';
import { SnackbarProps } from 'components/Snackbar';
import { GearFilterPanel } from 'components/GearFilterPanel/GearFilterPanel';
import { GearSortDropdown, SortData } from 'components/GearSortDropdown';

import cx from 'classnames';
import styles from '../UserProfile.module.scss';

export type ListingsTabProps = {
  profile: CustomerProfile;
  username: string;
  setSnackbarMessage: Dispatch<SnackbarProps | null>;
  toggleFavorite: (slug: string, isFavorite: boolean) => Promise<boolean>;
  onShare: (slug: string, owner: Partial<CustomerProfile>, isSold: boolean) => void;
  onComment: (slug: string) => void;
  listingData: CustomerGearResponse;
  filters: GearFilters;
};

const initialSorting: SortData = {
  sort: 'posted_at',
  order: 'desc',
};

export const LISTING_GEAR_LIMIT = 12;

export const ListingsTab = ({
  listingData,
  username,
  toggleFavorite,
  onComment,
  onShare,
  filters,
}: ListingsTabProps) => {
  const [currentListingGear, setCurrentListingGear] = useState(listingData.hits || []);
  const [totalPages, setTotalPages] = useState(Math.ceil(listingData.total / LISTING_GEAR_LIMIT));
  const [currentPage, setCurrentPage] = useState(1);
  const [wholeItemsCount, setWholeItemsCount] = useState(listingData.total);
  const [filterQueryParams, setFilterQueryParams] = useState<QueryParams>({ page: 1, status: 'all' });
  const [sorting, setSorting] = useState<SortData>(initialSorting);
  const [readyToLoad, setReadyToLoad] = useState(true);
  const isDelayedLoadingSet = useRef(false);

  useEffect(() => {
    setCurrentPage(1);
    loadGear(true).then();
  }, [filterQueryParams, sorting]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadGear = async (force?: boolean) => {
    if (!readyToLoad && !isDelayedLoadingSet.current) {
      isDelayedLoadingSet.current = true;
      force && setTimeout(() => loadGear(force), 300);
      return;
    }
    if (readyToLoad && isDelayedLoadingSet.current) {
      isDelayedLoadingSet.current = false;
    }

    setReadyToLoad(false);
    const response = await getCustomerGearList(username, {
      ...filterQueryParams,
      ...sorting,
      page: force ? 1 : currentPage + 1,
      limit: LISTING_GEAR_LIMIT,
    });
    if (response.ok && response.hits) {
      setCurrentPage(force ? 1 : currentPage + 1);
      setTotalPages(Math.ceil(response.total / LISTING_GEAR_LIMIT));
      setCurrentListingGear((previous) => {
        if (force) {
          return response.hits;
        }
        return [...previous, ...response.hits];
      });
      setWholeItemsCount(response.total);
    }
    setReadyToLoad(true);
  };

  return (
    <div className={styles.tabContentWrapper}>
      <div className={cx(styles.content, styles.contentS)}>
        <div className={styles.contentBlock}>
          <GearFilterPanel
            filters={filters}
            setFilterQueryParams={setFilterQueryParams}
            filterFetcher={(query: QueryParams) => getCustomerGearFilters(username as string, query)}
            presetFilters={filterQueryParams}
          />
        </div>
      </div>

      <div className={cx(styles.content, styles.contentL)}>
        <div className={styles.contentBlock}>
          <div className={cx(styles.contentBlockHeader)}>
            <div className={styles.contentBlockHeaderTitle}>
              {formatNumberToLetteredValue(wholeItemsCount)} listings posted
            </div>
            <GearSortDropdown sort={sorting.sort} order={sorting.order} onChange={setSorting} />
          </div>
          <div className={styles.contentCardGreed}>
            {!!currentListingGear.length &&
              currentListingGear.map((listGear) => (
                <GearCard
                  key={listGear.id}
                  {...listGear}
                  toggleFavorite={toggleFavorite}
                  onShare={onShare}
                  onComment={onComment}
                />
              ))}
            {totalPages > 1 && totalPages !== currentPage && <LazyLoader isActive={readyToLoad} callback={loadGear} />}
          </div>
        </div>
      </div>
    </div>
  );
};
